/**
 * FeatureTiers
 * 
 * Utility to manage feature access based on subscription tiers
 * Designed to support scaling to 2.5M students within two years
 * Integrates with the Morpheus Protocol token economy
 */

const FeatureTiers = {
  // Tier definitions aligned with the database user roles and subscription models
  TIERS: {
    // NEW SIMPLIFIED STRUCTURE
    GUEST: 'guest',         // Free tier (includes library features)
    UNLIMITED: 'unlimited', // Individual students ($89/year)
    HOME: 'home',          // Family plan up to 5 students ($199/year)
    EDUCATOR: 'teacher',    // Schools/Teachers (contact sales)
    ADMIN: 'admin',         // Administrative access

    // LEGACY TIERS (for existing users only)
    LIBRARY: 'library',     // LEGACY: Maps to guest
    STUDENT: 'student',     // LEGACY: Maps to unlimited features
    PREMIUM: 'premium',     // LEGACY: Maps to unlimited features
    EXPERT: 'expert',       // LEGACY: Maps to unlimited features
  },

  // Features with their minimum required tier
  FEATURES: {
    // Core features (available to all, including guests with limitations)
    VOICE_TUTORING: 'guest',           // Limited minutes for guests
    BASIC_VISUALIZATION: 'guest',      // Basic diagrams and charts
    MATH_TUTORING: 'guest',            // Limited questions for guests
    TAXONOMY_REMEMBER: 'guest',        // Basic recall questions
    TAXONOMY_UNDERSTAND: 'guest',      // Basic understanding questions
    FRIEND_TRAINING_PREVIEW: 'guest',  // Can join 2 friend sessions (viral hook)
    
    // Library tier features (reading-focused)
    READING_DISCUSSIONS: 'library',    // Book discussions and predictions
    READING_ATMOSPHERE: 'library',     // Sound effects for stories
    READING_PROGRESS: 'library',       // Reading session tracking
    READING_INSIGHTS: 'library',       // Save reading observations
    
    // LEGACY: Basic paid tier features (student tier - $19/year, discontinued for new signups)
    // These features are maintained for existing student tier users only
    UNLIMITED_QUESTIONS: 'student',    // No question limits (LEGACY)
    UNLIMITED_VOICE: 'student',        // No voice minutes limits (LEGACY)
    BASIC_PROGRESS_TRACKING: 'student', // Basic progress indicators (LEGACY)
    PERSONALIZED_LEARNING: 'student',  // Adaptive learning paths (LEGACY)
    TAXONOMY_APPLY: 'student',         // Higher-level application questions (LEGACY)
    SESSION_HISTORY: 'student',        // Access past tutoring sessions (LEGACY)
    FRIEND_TRAINING_JOIN: 'student',   // Can join friend training (LEGACY)
    
    // Unlimited tier features (NEW - includes all former premium/expert features)
    SCREEN_SHARING: 'unlimited',         // Share screen for homework help with AI
    PROGRESS_TRACKING: 'unlimited',      // Advanced progress tracking and analytics
    TAXONOMY_ANALYZE: 'unlimited',       // Advanced analytical questions
    TAXONOMY_EVALUATE: 'unlimited',      // Evaluation-level questions
    TAXONOMY_CREATE: 'unlimited',        // Creation-level questions
    ADVANCED_VISUALIZATIONS: 'unlimited', // Complex interactive visualizations
    LEARNING_PORTFOLIO: 'unlimited',     // Verified portfolio of achievements
    GITHUB_BOUNTIES: 'unlimited',        // GitHub bounty participation
    MARKETPLACE_ACCESS: 'unlimited',     // Access to purchase model tutors
    STRATEGY_MARKETPLACE: 'unlimited',   // Access advanced tutoring strategies
    TOKEN_ECONOMY: 'unlimited',          // Participate in token economy (Unlimited and Home users only)
    CURIOSITY_METRICS: 'unlimited',      // Advanced curiosity analytics and insights
    FRIEND_TRAINING_HOST: 'unlimited',   // Can start friend training sessions
    FRIEND_NETWORK_EFFECTS: 'unlimited', // Full network effect bonuses
    COLLABORATIVE_BOUNTIES: 'unlimited', // Team-based bounty participation
    
    // Unlimited tier features (continued - advanced capabilities)
    TOKEN_MULTIPLIER: 'unlimited',        // 2x token earning rate
    TOKEN_INVESTMENT: 'unlimited',        // Token investment with interest
    TOKEN_CREATION: 'unlimited',          // Limited token creation ability
    LEARNING_ANALYSIS: 'unlimited',       // Advanced learning style analysis
    PROGRESS_PREDICTION: 'unlimited',     // AI-powered knowledge gap prediction
    CUSTOM_STUDY_SCHEDULE: 'unlimited',   // Personalized study scheduling
    STUDY_GUIDE_CREATION: 'unlimited',    // Generate shareable study guides
    BOUNTY_CREATION_LIMITED: 'unlimited', // Create limited bounties
    SPECIALIZED_TUTORS: 'unlimited',      // Access to domain expert tutors
    PORTFOLIO_VERIFICATION: 'unlimited',  // Enhanced credential verification
    FRIEND_TRAINING_ANALYTICS: 'unlimited', // Advanced cross-training insights
    FRIEND_PERFORMANCE_ANALYSIS: 'unlimited', // Detailed friend collaboration metrics
    
    // Home tier features (NEW - family-specific features only)
    // Note: Home tier gets ALL Unlimited features PLUS these family-specific features
    // Home tier = 5 Unlimited accounts (1 admin + 4 students) with family management
    FAMILY_DASHBOARD: 'home',           // Multi-student progress dashboard
    FAMILY_ANALYTICS: 'home',           // Cross-child learning analytics
    FAMILY_COLLABORATION_BONUS: 'home', // Bonus tokens for sibling collaboration
    FAMILY_PROGRESS_REPORTS: 'home',    // Weekly family learning reports
    FAMILY_LEADERBOARDS: 'home',        // Family-wide leaderboards and challenges
    GROUP_MANAGEMENT: 'home',           // Manage family/group members
    
    // Features for future implementation - currently inactive
    // VISUALIZATION_TEMPLATES: 'expert', // Custom visualization templates - FUTURE
    // EXPERT_STUDY_GROUPS: 'expert',     // Private study groups - FUTURE
    
    // Teacher features
    MARKETPLACE_SELLING: 'teacher',    // Ability to sell models on marketplace
    STUDENT_MANAGEMENT: 'teacher',     // Manage student accounts and progress
    CURRICULUM_BUILDER: 'teacher',     // Create curriculum content
    CLASS_ANALYTICS: 'teacher',        // View class performance analytics
    BOUNTY_CREATION: 'teacher',        // Create bounties for students
    DEPLOYMENT_TOOLS: 'teacher',       // Deploy models to various environments
    
    // Admin features
    SYSTEM_CONFIGURATION: 'admin',     // Configure system settings
    USER_MANAGEMENT: 'admin',          // Manage all user accounts
    TOKEN_MANAGEMENT: 'admin',         // Manage token economy parameters
    ANALYTICS_DASHBOARD: 'admin'       // Full system analytics
  },

  // Guest feature limits
  GUEST_LIMITS: {
    VOICE_MINUTES: 30,                 // Free voice minutes
    VISUALIZATION_TYPES: 3,            // Basic visualization types
    TAXONOMY_LEVEL_LIMIT: 2,           // Up to "understand" level only
    FRIEND_TRAINING_SESSIONS: 2,       // Can join 2 friend sessions (viral hook)
    FRIEND_TRAINING_DURATION: 15       // 15 minutes per session max
  },

  // Per-use pricing for special features (not included in subscription)
  PAY_PER_USE: {
    MODEL_TRAINING: {
      BASE_PRICE: 5.00,                // Base price per model training
      HOURLY_RATE: 2.50,               // Additional cost per training hour
      COMPLEXITY_MULTIPLIER: {         // Multipliers based on model complexity
        BASIC: 1.0,
        INTERMEDIATE: 1.5,
        ADVANCED: 2.5
      }
    },
    MARKETPLACE: {
      COMMISSION_RATE: 0.15,           // 15% commission on marketplace sales
      MINIMUM_PRICE: 1.00,             // Minimum price for a marketplace model
      FEATURED_LISTING_FEE: 5.00       // Fee to feature a listing on marketplace
    }
  },

  // Tier hierarchy for comparison
  TIER_LEVELS: {
    'guest': 0,
    'library': 0.5,  // Between guest and student - reading focused
    'student': 1,     // LEGACY
    'premium': 2,     // LEGACY  
    'expert': 3,      // LEGACY
    'unlimited': 3,   // NEW: Same feature level as expert
    'home': 3.5,     // NEW: Higher than unlimited, includes family features
    'teacher': 4,
    'admin': 5
  },

  // Simplified 4-tier pricing structure
  PRICING: {
    // New simplified pricing (post-cognitive load reduction)
    FREE: 0,                          // Free tier
    UNLIMITED_ANNUAL: 89,             // $89 per year (combines Premium/Expert value)
    UNLIMITED_MONTHLY: 9.99,          // $9.99 per month
    HOME_ANNUAL: 199,                 // $199 per year for up to 5 students
    HOME_MONTHLY: 19.99,              // $19.99 per month for families
    TEACHER_ANNUAL: 0,                // Contact sales (educators)
    
    // Annual savings calculations
    UNLIMITED_ANNUAL_SAVING: 26,       // 26% savings with annual vs monthly ($89 vs $119.88)
    HOME_ANNUAL_SAVING: 17,            // 17% savings with annual vs monthly ($199 vs $239.88)
    
    // Family plan value calculations
    HOME_VALUE_PER_STUDENT_ANNUAL: 39.80,  // $199 ÷ 5 students
    HOME_VALUE_PER_STUDENT_MONTHLY: 4.00,  // $19.99 ÷ 5 students
    HOME_SAVINGS_VS_INDIVIDUAL: 246,       // $445 - $199 = $246 savings per year
    
    // Savings percentages for new simplified tiers
    getSavingsPercentage: function(tier) {
      if (tier === 'unlimited') {
        return this.UNLIMITED_ANNUAL_SAVING; // 26% savings annual vs monthly
      } else if (tier === 'home') {
        return this.HOME_ANNUAL_SAVING; // 17% savings annual vs monthly  
      }
      return 0;
    },
    
    // Get family plan savings vs individual plans
    getFamilySavings: function() {
      return this.HOME_SAVINGS_VS_INDIVIDUAL; // $246/year savings
    },
    
    // Get days remaining until price increase
    getDaysUntilIncrease: function() {
      // Price increase date has passed, no longer showing price increase warnings
      return 0;
    }
  },

  /**
   * Check if user has access to a feature
   * @param {string} featureName - The feature to check access for
   * @param {Object} user - The user object including tier information
   * @param {string} [defaultTier] - Default tier if user is not provided
   * @returns {boolean} - Whether the user has access to the feature
   */
  hasAccess(featureName, user, defaultTier = 'guest') {
    // Determine user's tier
    const userTier = this.getUserTier(user) || defaultTier;
    
    // Check if feature exists
    if (!this.FEATURES[featureName]) {
      console.warn(`Feature "${featureName}" is not defined in FeatureTiers`);
      return false;
    }
    
    // Get required tier level for feature
    const requiredTierName = this.FEATURES[featureName];
    const requiredTierLevel = this.TIER_LEVELS[requiredTierName];
    
    // Get user's tier level
    const userTierLevel = this.TIER_LEVELS[userTier];
    
    // User can access if their tier level is >= required tier level
    return userTierLevel >= requiredTierLevel;
  },
  
  /**
   * Legacy function maintained for backward compatibility
   * @param {Object} user - The user object (optional)
   * @returns {number} - Always returns Infinity as we no longer count questions
   */
  getRemainingQuestions(user) {
    return Infinity;
  },
  
  /**
   * Legacy function maintained for backward compatibility
   * @returns {number} - Always returns Infinity as we no longer count questions
   */
  incrementQuestionCount() {
    return Infinity;
  },
  
  /**
   * Get remaining voice minutes for guests from localStorage
   * @param {Object} user - The user object (optional)
   * @returns {number} - Number of minutes remaining, or Infinity for paid tiers
   */
  getRemainingVoiceMinutes(user) {
    const userTier = this.getUserTier(user);
    
    // If paid tier, return Infinity
    if (userTier !== 'guest') {
      return Infinity;
    }
    
    // For guests, check localStorage
    const usedMinutes = parseFloat(localStorage.getItem('morpheus_free_voice_minutes_used') || '0');
    return Math.max(0, this.GUEST_LIMITS.VOICE_MINUTES - usedMinutes);
  },
  
  /**
   * Increment the voice minutes counter
   * @param {number} minutes - Minutes to add to the counter
   * @returns {number} - Number of minutes remaining after increment
   */
  incrementVoiceMinutes(minutes) {
    // Get current usage
    const usedMinutes = parseFloat(localStorage.getItem('morpheus_free_voice_minutes_used') || '0');
    
    // Add minutes
    const newCount = usedMinutes + minutes;
    
    // Save back to localStorage
    localStorage.setItem('morpheus_free_voice_minutes_used', newCount.toString());
    
    // Return remaining
    return Math.max(0, this.GUEST_LIMITS.VOICE_MINUTES - newCount);
  },
  
  /**
   * Reset the guest counters (for testing or development)
   */
  resetGuestCounters() {
    localStorage.setItem('morpheus_free_voice_minutes_used', '0');
  },
  
  /**
   * Calculate model training price
   * @param {Object} options - Training options
   * @param {string} options.complexity - Model complexity (BASIC, INTERMEDIATE, ADVANCED)
   * @param {number} options.estimatedHours - Estimated training hours
   * @returns {number} - Estimated price
   */
  calculateModelTrainingPrice(options = {}) {
    const complexity = options.complexity || 'BASIC';
    const estimatedHours = options.estimatedHours || 1;
    
    const basePrice = this.PAY_PER_USE.MODEL_TRAINING.BASE_PRICE;
    const hourlyRate = this.PAY_PER_USE.MODEL_TRAINING.HOURLY_RATE;
    const complexityMultiplier = this.PAY_PER_USE.MODEL_TRAINING.COMPLEXITY_MULTIPLIER[complexity] || 1.0;
    
    return (basePrice + (hourlyRate * estimatedHours)) * complexityMultiplier;
  },
  
  /**
   * Calculate marketplace commission
   * @param {number} salePrice - Model sale price
   * @returns {Object} - Commission details
   */
  calculateMarketplaceCommission(salePrice) {
    const commissionRate = this.PAY_PER_USE.MARKETPLACE.COMMISSION_RATE;
    const commissionAmount = salePrice * commissionRate;
    const sellerReceives = salePrice - commissionAmount;
    
    return {
      salePrice,
      commissionRate,
      commissionAmount,
      sellerReceives
    };
  },
  
  /**
   * Map subscription tier names to feature tier constants
   * Handles both new and legacy tier naming
   * @param {string} tier - Subscription tier from backend
   * @returns {string} - Feature tier constant
   */
  mapSubscriptionTierToFeatureTier(tier) {
    const tierMapping = {
      // New tiers
      'unlimited': this.TIERS.UNLIMITED,
      'home': this.TIERS.HOME,              // Home/family plan - tier 3.5 includes all unlimited features plus family features
      'family': this.TIERS.UNLIMITED,      // Alternative family plan name
      'educator': this.TIERS.EDUCATOR,
      'enterprise': this.TIERS.ENTERPRISE,

      // Legacy tiers (existing users)
      'expert': this.TIERS.UNLIMITED,      // LEGACY: expert maps to unlimited
      'expert-family': this.TIERS.UNLIMITED, // LEGACY: expert-family maps to unlimited
      'premium': this.TIERS.UNLIMITED,     // LEGACY: premium maps to unlimited
      'student': this.TIERS.UNLIMITED,     // LEGACY: student tier maps to unlimited

      // Library
      'library': this.TIERS.LIBRARY,

      // Free tier
      'guest': this.TIERS.GUEST
    };

    return tierMapping[tier] || this.TIERS.GUEST;
  },

  /**
   * Get default tier based on user role (when no subscription exists)
   * @param {string} role - User's role (student, teacher, parent, etc.)
   * @returns {string} - Default feature tier for that role
   */
  getRoleDefaultTier(role) {
    switch (role) {
      case 'system_admin':
        return this.TIERS.ENTERPRISE;
      case 'district_admin':
      case 'school_admin':
      case 'teacher':
        return this.TIERS.EDUCATOR;  // Teachers get free educator tools
      case 'administrator':  // Legacy admin role
        return this.TIERS.ADMIN;
      case 'student':
      case 'parent':
      case 'registered':
      default:
        return this.TIERS.GUEST;  // Default to guest tier for unpaid users
    }
  },

  /**
   * Determine user's tier from authentication data
   * Priority: subscription tier > school license > role-based default
   * @param {Object} user - User object from authentication
   * @returns {string} - The user's tier
   */
  getUserTier(user) {
    if (!user) {
      return this.TIERS.GUEST;
    }

    // PRIORITY 1: Check if user is a library account (special case)
    if (user.account_type === 'library') {
      return this.TIERS.LIBRARY;
    }

    // PRIORITY 2: Check individual/family subscription (from user_subscriptions table)
    if (user.subscription && user.subscription.tier) {
      // Check subscription status - only use if active
      if (user.subscription.status && user.subscription.status === 'active') {
        const tier = this.mapSubscriptionTierToFeatureTier(user.subscription.tier);

        // Special case: Family members (have family_plan_id) should show "Unlimited" not "Home"
        // Only the plan owner (no family_plan_id) should see "Home"
        if ((tier === this.TIERS.HOME || user.subscription.tier === 'home') && user.family_plan_id) {
          return this.TIERS.UNLIMITED;
        }

        return tier;
      }
      // If subscription exists but isn't active, fall through to next priority
    }

    // PRIORITY 3: Check school license (institutional licensing)
    if (user.school_license_tier) {
      const tier = this.mapSubscriptionTierToFeatureTier(user.school_license_tier);
      return tier;
    }

    // PRIORITY 4: Direct tier column (manual/comped upgrades — set via
    // UPDATE users SET tier='unlimited' with no Stripe subscription).
    // Mirrors the server's determine_user_tier third check.
    if (user.tier && user.tier !== 'guest') {
      const directTier = this.mapSubscriptionTierToFeatureTier(user.tier);
      if (directTier && directTier !== this.TIERS.GUEST) {
        return directTier;
      }
    }

    // PRIORITY 5: Fall back to role-based default tier
    const defaultTier = this.getRoleDefaultTier(user.role);
    return defaultTier;
  },
  
  /**
   * Get upgrade CTA text for a feature
   * @param {string} featureName - The feature to get CTA for
   * @returns {string} - CTA text for upgrading
   */
  getUpgradeCTA(featureName) {
    const requiredTier = this.FEATURES[featureName];
    
    // Create more tailored messages for each feature based on its tier
    switch (requiredTier) {
      case this.TIERS.STUDENT:
        // LEGACY tier - for existing users only
        return `This feature requires a paid subscription`;
      
      case this.TIERS.PREMIUM:
        // LEGACY tier - for existing users only  
        return `This feature requires a paid subscription`;
      
      case this.TIERS.EXPERT:
        // LEGACY tier - for existing users only
        return `This feature requires a paid subscription`;
        
      case this.TIERS.UNLIMITED:
        // NEW tier - main upgrade target
        switch (featureName) {
          case 'TOKEN_MULTIPLIER':
            return `Get Unlimited access to earn tokens 2x faster for just $${this.PRICING.UNLIMITED_ANNUAL}/year`;
          
          case 'TOKEN_INVESTMENT':
            return `Invest tokens and earn interest with Unlimited access - $${this.PRICING.UNLIMITED_ANNUAL}/year`;
          
          case 'LEARNING_ANALYSIS':
            return `Get advanced AI learning analysis with Unlimited access - $${this.PRICING.UNLIMITED_ANNUAL}/year`;
          
          case 'PROGRESS_PREDICTION':
            return `Let AI predict and fill your knowledge gaps with Unlimited access - $${this.PRICING.UNLIMITED_ANNUAL}/year`;
          
          case 'CUSTOM_STUDY_SCHEDULE':
            return `Get AI-optimized study schedules with Unlimited access - $${this.PRICING.UNLIMITED_ANNUAL}/year`;
          
          case 'STUDY_GUIDE_CREATION':
            return `Generate comprehensive study guides automatically with Unlimited access - $${this.PRICING.UNLIMITED_ANNUAL}/year`;
          
          case 'SPECIALIZED_TUTORS':
            return `Access domain-specific expert tutors with Unlimited access - $${this.PRICING.UNLIMITED_ANNUAL}/year`;
          
          default:
            return `Get Unlimited access for $${this.PRICING.UNLIMITED_ANNUAL}/year to unlock all features`;
        }
      
      case this.TIERS.HOME:
        // NEW tier - family upgrade target
        return `Get Home plan for your family - $${this.PRICING.HOME_ANNUAL}/year for up to 5 students`;
      
      case this.TIERS.TEACHER:
        return 'Contact sales for educator pricing';
      
      case this.TIERS.ADMIN:
        return 'This feature requires administrative access';
      
      default:
        return 'Upgrade your account to access this feature';
    }
  },
  
  /**
   * Format a user-friendly tier name
   * @param {string} tierName - Internal tier name
   * @returns {string} - User-friendly tier name
   */
  formatTierName(tierName) {
    const tierNames = {
      [this.TIERS.GUEST]: 'Guest',
      [this.TIERS.LIBRARY]: 'Library Partner',
      [this.TIERS.STUDENT]: 'Student',      // LEGACY
      [this.TIERS.PREMIUM]: 'Premium',      // LEGACY
      [this.TIERS.EXPERT]: 'Expert',        // LEGACY
      [this.TIERS.UNLIMITED]: 'Unlimited',  // NEW
      [this.TIERS.HOME]: 'Home',           // NEW
      [this.TIERS.TEACHER]: 'Teacher',
      [this.TIERS.ADMIN]: 'Administrator',
      'registered': 'Registered (Unsubscribed)'
    };
    
    return tierNames[tierName] || 'Unknown Tier';
  },
  
  /**
   * Get features available for a specific tier
   * @param {string} tierName - Tier to get features for
   * @returns {Array} - List of features available for the tier
   */
  getTierFeatures(tierName) {
    const tierLevel = this.TIER_LEVELS[tierName];
    if (tierLevel === undefined) return [];
    
    // Get all features available to this tier
    return Object.entries(this.FEATURES)
      .filter(([_, requiredTier]) => this.TIER_LEVELS[requiredTier] <= tierLevel)
      .map(([featureName, _]) => featureName);
  },
  
  /**
   * Get tier comparison data for the pricing page
   * @returns {Object} - Tier comparison data
   */
  getTierComparisonData() {
    // Calculate the days until price increase
    const daysRemaining = this.PRICING.getDaysUntilIncrease();
    const showPriceIncrease = daysRemaining > 0;
    
    return {
      tiers: [
        {
          id: this.TIERS.GUEST,
          name: 'Guest',
          price: 'Free',
          description: 'Try before you buy',
          features: [
            '30 minutes of voice tutoring',
            'Basic visualizations',
            'Remember & understand questions',
            'No account required'
          ],
          limitations: [
            'Limited voice time (30 min)',
            'No progress tracking',
            'No personalization'
          ]
        },
        {
          id: this.TIERS.LIBRARY,
          name: 'Library Partner',
          price: 'Free',
          description: 'Reading companion through your local library',
          special: true,
          features: [
            'Reading discussions and predictions',
            'Atmospheric sound effects for stories',
            'Reading progress tracking', 
            'Reading insights and observations',
            'Available anywhere, anytime',
            'No math or science tutoring'
          ],
          limitations: [
            'Reading and literature only',
            'No math or science help',
            'No token economy access',
            'No advanced features'
          ]
        },
        {
          id: this.TIERS.STUDENT,
          name: 'Student',
          price: `$${this.PRICING.STUDENT_ANNUAL}/year`,
          futurePrice: showPriceIncrease ? this.PRICING.PRICE_INCREASE.STUDENT_ANNUAL : null,
          priceIncreaseDays: daysRemaining,
          savingsPercent: this.PRICING.getSavingsPercentage('student'),
          description: 'Unlimited tutoring',
          features: [
            'Unlimited math tutoring questions',
            'Unlimited voice tutoring',
            'Personalized learning',
            'Progress tracking',
            'Remember, understand & apply questions',
            'Session history'
          ],
          limitations: [
            'Basic visualization types',
            'No token economy access',
            'No advanced taxonomy levels',
            'No GitHub integration',
            'No marketplace access'
          ]
        },
        {
          id: this.TIERS.PREMIUM,
          name: 'Premium',
          price: `$${this.PRICING.PREMIUM_ANNUAL}/year`,
          monthlyPrice: `$${this.PRICING.PREMIUM_MONTHLY}/month`,
          annualSavings: this.PRICING.PREMIUM_MONTHLY_SAVING,
          hasMonthlyOption: true,
          futurePrice: showPriceIncrease ? this.PRICING.PRICE_INCREASE.PREMIUM_ANNUAL : null,
          priceIncreaseDays: daysRemaining,
          savingsPercent: this.PRICING.getSavingsPercentage('premium'),
          description: 'Advanced learning',
          isRecommended: true,
          features: [
            'All Student features',
            'Token economy participation (basic)',
            'Advanced interactive visualizations (limited set)',
            'All taxonomy levels (analyze, evaluate, create)',
            'GitHub bounty participation (participation only)',
            'Learning portfolio (basic)',
            'Access to marketplace tutors (standard selection)',
            'Premium tutoring strategies'
          ],
          payPerUse: [
            'Model training (pay per training)',
          ],
          limitations: [
            'Limited token earnings (1x rate)',
            'Basic learning style analysis',
            'No personalized study planning',
            'No study guide generation',
            'No specialized tutors'
          ]
        },
        {
          id: this.TIERS.EXPERT,
          name: 'Expert',
          price: `$${this.PRICING.EXPERT_ANNUAL}/year`,
          monthlyPrice: `$${this.PRICING.EXPERT_MONTHLY}/month`,
          annualSavings: this.PRICING.EXPERT_MONTHLY_SAVING,
          hasMonthlyOption: true,
          futurePrice: showPriceIncrease ? this.PRICING.PRICE_INCREASE.EXPERT_ANNUAL : null,
          priceIncreaseDays: daysRemaining,
          savingsPercent: this.PRICING.getSavingsPercentage('expert'),
          description: 'Accelerated growth',
          features: [
            'All Premium features',
            '2x token earning rate',
            'Advanced learning style analysis',
            'Personalized study planning',
            'Study guide generation',
            'Limited bounty creation',
            'Access to specialized tutors',
            'Enhanced portfolio verification'
          ],
          familyPlan: {
            price: this.PRICING.EXPERT_FAMILY,
            futurePrice: showPriceIncrease ? this.PRICING.PRICE_INCREASE.EXPERT_FAMILY : null,
            savingsPercent: this.PRICING.getSavingsPercentage('expert-family'),
            description: '3-5 students'
          },
          payPerUse: [
            'Model training (pay per training)',
          ],
          limitations: []
        },
        {
          id: this.TIERS.UNLIMITED,
          name: 'Unlimited',
          price: `$${this.PRICING.UNLIMITED_ANNUAL}/year`,
          monthlyPrice: `$${this.PRICING.UNLIMITED_MONTHLY}/month`,
          annualSavings: this.PRICING.UNLIMITED_ANNUAL_SAVING,
          hasMonthlyOption: true,
          savingsPercent: this.PRICING.getSavingsPercentage('unlimited'),
          description: 'Complete learning solution',
          isRecommended: true,
          features: [
            'All Expert features plus:',
            'Full token economy access',
            'Token investment with interest',
            'Advanced progress analytics',
            'AI-powered knowledge gap prediction',
            'Custom study scheduling',
            'Study guide creation & sharing',
            'Access to specialized domain tutors',
            'Enhanced portfolio verification',
            'Friend training host privileges',
            'Collaborative bounty participation'
          ],
          limitations: []
        },
        {
          id: this.TIERS.HOME,
          name: 'Home',
          price: `$${this.PRICING.HOME_ANNUAL}/year`,
          monthlyPrice: `$${this.PRICING.HOME_MONTHLY}/month`,
          annualSavings: this.PRICING.HOME_ANNUAL_SAVING,
          hasMonthlyOption: true,
          savingsPercent: this.PRICING.getSavingsPercentage('home'),
          description: 'Family plan for up to 5 students',
          features: [
            'All Unlimited features for each family member',
            'Family progress dashboard',
            'Cross-child learning analytics',
            'Family collaboration bonuses',
            'Weekly family progress reports',
            'Family-wide leaderboards & challenges',
            'Up to 5 student accounts',
            `Save $${this.PRICING.getFamilySavings()}/year vs individual plans`
          ],
          limitations: []
        },
        {
          id: this.TIERS.TEACHER,
          name: 'Teacher',
          price: 'Marketplace creator plan',
          description: 'For educators',
          features: [
            'All Expert features',
            'Student management',
            'Curriculum builder',
            'Class analytics',
            'Bounty creation',
            'Deployment options',
            'Sell models on marketplace'
          ],
          payPerUse: [
            'Model training (pay per training)',
            'Featured marketplace listings'
          ],
          marketplaceInfo: '15% commission on marketplace sales',
          limitations: []
        }
      ],
      payPerUse: {
        modelTraining: {
          basePrice: this.PAY_PER_USE.MODEL_TRAINING.BASE_PRICE,
          hourlyRate: this.PAY_PER_USE.MODEL_TRAINING.HOURLY_RATE,
          complexityMultipliers: this.PAY_PER_USE.MODEL_TRAINING.COMPLEXITY_MULTIPLIER
        },
        marketplace: {
          commissionRate: this.PAY_PER_USE.MARKETPLACE.COMMISSION_RATE * 100, // Convert to percentage
          minimumPrice: this.PAY_PER_USE.MARKETPLACE.MINIMUM_PRICE,
          featuredListingFee: this.PAY_PER_USE.MARKETPLACE.FEATURED_LISTING_FEE
        }
      }
    };
  }
};

export default FeatureTiers;